import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { filterFactory } from "@similarweb/pro-logic-components";
import type { SelectOption } from "@similarweb/pro-logic-components/dist/filter-factory/src/compound";
import { Box } from "@similarweb/ui-components/foundations";

import categoryService, { ALL } from "app/common/services/categoryService";
import type { ICategory } from "app/common/services/categoryService.types";

export interface IIndustryPublishersFilterState {
    publisherIndustry?: string;
}

const Filters = filterFactory.createTypedFilters<IIndustryPublishersFilterState>();

interface IIndustryPublishersFiltersProps {
    // The selected value is a category `forApi` (`$`-prefixed, e.g. "$Sports/Basketball").
    selectedIndustry?: string;
    onFilterChange: (value: IIndustryPublishersFilterState) => void;
}

export const IndustryPublishersFilters = ({ selectedIndustry, onFilterChange }: IIndustryPublishersFiltersProps) => {
    const { translate } = useTranslate();
    const industriesForFilter: SelectOption[] = useMemo(
        () =>
            categoryService
                .getCategories(false, { excludeAdult: true })
                .filter((item) => item.id !== ALL)
                .flatMap(
                    (item) =>
                        [
                            {
                                id: item.forApi,
                                value: item.forApi,
                                label: item.text,
                                icon: <i className={item.icon} />,
                            } as SelectOption,
                            ...(Array.isArray(item?.children)
                                ? item.children.map((son: ICategory) => ({
                                      id: son.forApi,
                                      value: son.forApi,
                                      label: son.text,
                                      icon: <Box width="14px" />,
                                  }))
                                : []),
                        ] as SelectOption[],
                )
                .map((item) => ({ ...item, children: undefined })),
        [],
    );

    const selectedLabel =
        industriesForFilter.find((option) => option.value === selectedIndustry)?.label ??
        translate("ad.intelligence.advertiser.publishers.filters.industry.default_label");

    return (
        <Filters.Provider
            state={{ publisherIndustry: selectedIndustry }}
            onChange={onFilterChange}
        >
            <Filters.Select
                name="publisherIndustry"
                label={selectedLabel}
                options={industriesForFilter}
                isDisabled={!industriesForFilter?.length}
                withSearch
            />
        </Filters.Provider>
    );
};
